/**
 * 生成器（索引 Issue #498 の段階 2。設計文書 §5）。**純関数**。
 *
 * ソースが出したアンカー列と型の宣言から、**帯の初期配置**を作る（時刻を決めるのは `align.js`）。
 * 文書・エディタの概念は持ち込まない。
 *
 *   opens(anchor, state) / closes(anchor, open, state)   簡単な場合。アンカーごとに呼ばれ、開くならデータを返す
 *   build(ctx)                                           複雑な場合。帯を好きなだけ返す
 *
 * どちらも返すのは `{ anchor, endAnchor?, data, sub?, durationMs?, overlapKey?, overlaps?, commitMs? }`。
 * `data` はコアにとって不透明（`payload` として描画側まで運ぶだけ）。
 *
 * **重ねの鍵**（`overlapKey`。Issue #556）: 整列（`align.js` の `sequential`）が「直前の帯と重ねてよいか」を引く名前。
 * 既定は型の id（今までどおり型単位）。帯ごとに変えたいときは、`build` の返り値に `overlapKey` / `overlaps` を載せるか、
 * 型に `overlapKey(data)` を宣言する（例: 同じ `move` でも「場からの破壊」どうしだけ重ねる）。
 *
 * **状態を進める時刻**（`commitMs`。Issue #566）: 帯の頭から何 ms で、開いたアンカーの状態（盤面など）を
 * 「そのアンカーの後」へ進めるか。無ければ null（＝今までどおりアンカーの時刻で進む）。
 * 実際にどの状態チャンネルがこれを見るかはソースが決める（`states.js::commitRows`）。
 */

/** 帯の id（型:アンカーのキー、1 アンカーから複数なら #sub）。乱数・時計を使わないので開くたびに同じ */
const anchoredId = (type, anchorKey, sub = null) => `${type}:${anchorKey}${sub == null ? '' : `#${sub}`}`;

/** 状態を進める時刻（帯の頭からの ms）。数でない・負は宣言なし（null） */
const commitOf = (v) => (Number.isFinite(v) && v >= 0 ? v : null);

/** opens / closes から帯の元を作る */
function fromRules(type, anchors) {
  const state = {};
  const out = [];
  const open = [];
  for (const anchor of anchors) {
    if (typeof type.closes === 'function') {
      for (let i = open.length - 1; i >= 0; i -= 1) {
        if (type.closes(anchor, open[i].data, state)) { open[i].endAnchor = anchor; open.splice(i, 1); }
      }
    }
    const data = type.opens(anchor, state);
    if (data) {
      const spec = { anchor, data };
      out.push(spec);
      if (typeof type.closes === 'function') open.push(spec);
    }
  }
  return out;
}

/**
 * 1 つの型から帯の元を集める。キーの無いアンカー（時刻の錨にならないもの）と `data` の無いものは落とす。
 */
function collectSpecs(type, anchors = [], ctx = {}) {
  const specs = typeof type.build === 'function' ? (type.build({ anchors, ...ctx }) ?? []) : fromRules(type, anchors);
  return specs.filter((s) => s?.anchor?.key && s.data != null);
}

/**
 * アンカー列 → 従属オブジェクト。
 * @param anchors `[{ key, ms, index, payload, ... }]`（並び順だけを見る。時刻は `build` が盤面を引くのに使う）
 * @param ctx `build` に渡すもの（`sourceId` はここから。ドメイン固有の材料は自由に足してよい）
 * @param registry 型のレジストリ
 * @returns `[{ id, type, trackKind, source, durationMs, endAnchorKey, endOffsetMs, layer, overlapKey, overlaps,
 *            commitMs, minDurationMs, payload }]` —— 同じ型の中はアンカーの並び順、型は登録順
 */
export function generateObjects(anchors = [], ctx = {}, registry) {
  const sourceId = ctx.sourceId ?? 'default';
  const out = [];
  for (const type of registry.list()) {
    const timing = type.timing ?? {};
    for (const spec of collectSpecs(type, anchors, ctx)) {
      const endAnchorKey = spec.endAnchor?.key ?? null;
      out.push({
        id: anchoredId(type.id, spec.anchor.key, spec.sub),
        type: type.id,
        trackKind: type.track ?? 'graphic',
        source: { sourceId, anchorKey: spec.anchor.key, sub: spec.sub ?? null },
        // 尺は「build が決めた値 → 型の宣言」。**人は上書きしない**（Issue #491）
        durationMs: endAnchorKey ? null : (spec.durationMs ?? timing.durationMs ?? 1000),
        endAnchorKey,
        endOffsetMs: endAnchorKey ? (timing.endOffsetMs ?? 0) : null,
        layer: type.layer ?? 'event',
        // 帯ごとの重ね方（無ければ型の宣言）。鍵は null なら型の id（`align.js` が補う）
        overlapKey: spec.overlapKey ?? (typeof type.overlapKey === 'function' ? (type.overlapKey(spec.data) ?? null) : null),
        overlaps: spec.overlaps ?? type.overlaps ?? {},
        // 開いたアンカーの状態を進める時刻（帯の頭からの ms。Issue #566）。無ければ null
        commitMs: commitOf(spec.commitMs),
        minDurationMs: timing.durationMs ?? 1000,
        payload: spec.data,
      });
    }
  }
  return out;
}
